import React, {useEffect} from "react";
import {useCsContext} from "../../state/CsInstance";
import {observer} from "mobx-react-lite";
import CompileButton from "../CompileButton/CompileButton";
import {EMPTY_EDITOR_PROMPT} from "../../state/codeStore";
// Frontend
import styles from "./Editor.module.scss";
import {ErrorBoundary} from "react-error-boundary";
import ErrorComponent from "../ErrorComponent/ErrorComponent";
// Monaco
import Editor from "@monaco-editor/react";

type Props = {
	heightStyle: string;
};

const EditorWrapper: React.FC<Props> = (props: Props) => {
	const {codeStore, uiStore, featureFlagStore, userInputStore} = useCsContext();
	const {heightStyle} = props;

	const onKeyDownHandler = (e: KeyboardEvent) => {
		uiStore.onKeyDownHandler(e, userInputStore.isEditorFocused);
	};
	useEffect(() => {
		// Add event listeners for keyboard and mouse events
		window.addEventListener("keydown", onKeyDownHandler);
		window.addEventListener("click", userInputStore.handleFocusEvent);

		// Cleanup
		return () => {
			window.removeEventListener("keydown", onKeyDownHandler);
			window.addEventListener("click", userInputStore.handleFocusEvent);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function handleEditorChange(value: string | undefined) {
		codeStore.hasUncompiledChanges = true;
		if (!codeStore.focusedFile) {
			return;
		}
		codeStore.setFileData(codeStore.focusedFile, {
			name: codeStore.focusedFile,
			value: value || "",
			attributes: codeStore.fileMap[codeStore.focusedFile]?.attributes,
		});
		codeStore.saveProjectToLocalStorage();
	}

	// customizable monaco editor options
	const options = {
		// Convert to pixels.  Unfortunately Monaco doesn't support rem, and it
		// would be a pain to do so manually, so we'll just assume 16px = 1rem.
		fontSize: uiStore.editorFontSize * 16, // Convert to pixels
		readOnly: codeStore.filesOpen.length === 0,
		automaticLayout: true, // automatically adjust editor size
		minimap: {enabled: false},
	};

	function renderEditor() {
		// If no files are open in a tab, show the default empty file content
		let path: string | undefined = undefined;
		let value = EMPTY_EDITOR_PROMPT;
		if (codeStore.focusedFile) {
			// Otherwise, show the code editor with the focused file content
			const currFile = codeStore.visibleFileMap[codeStore.focusedFile];
			path = currFile?.name;
			value = currFile?.value || "";
		}
		return (
			<Editor
				height={heightStyle}
				width="100%"
				theme="vs-light"
				path={path}
				options={options}
				language={featureFlagStore.usePython ? "python" : "java"}
				value={value}
				onChange={handleEditorChange}
			/>
		);
	}

	return (
		<ErrorBoundary FallbackComponent={ErrorComponent}>
			<div className={styles.editorToolbar}>
				{featureFlagStore.showExtraButtons && (
					<button
						className={styles.resetButton}
						onClick={codeStore.resetProject}
					>
						Reset
					</button>
				)}
				{!featureFlagStore.hideCompileButton && (
					<CompileButton disabled={!codeStore.hasUncompiledChanges} />
				)}
			</div>
			<div id="editor" className={styles.editor}>
				{renderEditor()}
			</div>
		</ErrorBoundary>
	);
};

export default observer(EditorWrapper);
